<template>
  <div>
    <reports-loader
      :client-id="clientId"
      :filters="filters"
      :result="{ items: reports, total: pagination.rowsNumber }"
      @loading="isLoading = $event"
      @update:result="onLoaded"
    />
    <q-table
      :columns="columns"
      :rows="reports"
      v-model:pagination="pagination"
      :loading="isLoading"
      wrap-cells
      @request="pagination = $event.pagination"
    >
      <template v-slot:body-cell-name="props">
        <q-td :props="props">
          <router-link :to="`/albums/${props.row.id}`" class="q-link">
            {{ props.value }}
          </router-link>
        </q-td>
      </template>
      <template v-slot:body-cell-dateRange="props">
        <q-td :props="props">
          <date-range
            :date-of-loss="props.row.client.dateOfLoss"
            :range="props.value" />
        </q-td>
      </template>
      <template v-slot:body-cell-createdAt="props">
        <q-td :props="props">
          <humanize-time :time="props.value" />
        </q-td>
      </template>
      <template v-slot:body-cell-lastModified="props">
        <q-td :props="props">
          <humanize-time :time="props.value" />
        </q-td>
      </template>
      <template v-slot:body-cell-actions="props">
        <q-td :props="props">
          <download-btn :report-id="props.row.id" label="" class="on-left" flat />
          <delete-report-btn :report="props.row" flat round @deleted="remove($event.id)" />
        </q-td>
      </template>
      <template v-slot:no-data>
        <message type="info" class="full-width q-my-sm" style="font-size: .9rem">
          No albums found.
        </message>
      </template>
    </q-table>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import {
  OpenDateRange,
  QTablePagination,
  QueryResult,
  Report,
  ReportFilters,
} from '@/type';
import HumanizeTime from '@/components/HumanizeTime.vue';
import Message from '@/components/message/Message.vue';
import DateRange from '../DateRange.vue';
import DeleteReportBtn from '../DeleteReportBtn.vue';
import DownloadBtn from './DownloadBtn.vue';
import ReportsLoader from '../ReportsLoader.vue';
import { types as reportTypes } from '..';

interface Data {
  columns: object[],
  pagination: QTablePagination,
  reports: Report[],
  isLoading: boolean,
}

export default defineComponent({
  components: {
    Message,
    HumanizeTime,
    DateRange,
    DeleteReportBtn,
    DownloadBtn,
    ReportsLoader,
  },
  props: {
    clientId: {
      type: Number,
      required: true,
    },
  },
  data(): Data {
    return {
      columns: [
        {
          name: 'name',
          label: 'Name',
          field: 'name',
          align: 'left',
        },
        {
          name: 'dateRange',
          label: 'Date Range',
          field(r: Report): OpenDateRange {
            return {
              start: r.startDate,
              end: r.endDate,
            };
          },
          align: 'left',
        },
        {
          name: 'createdAt',
          label: 'Created',
          field: 'createdAt',
          align: 'left',
        },
        {
          name: 'lastModified',
          label: 'Last Modified',
          field: 'lastModified',
          align: 'left',
        },
        {
          name: 'actions',
          label: '',
          align: 'right',
        },
      ],
      pagination: {
        page: 1,
        rowsPerPage: 5,
        rowsNumber: 0,
      },
      isLoading: false,
      reports: [],
    };
  },
  computed: {
    filters(): ReportFilters {
      return {
        types: [reportTypes.ALBUM],
        offset: (this.pagination.page - 1) * this.pagination.rowsPerPage,
        limit: this.pagination.rowsPerPage,
      };
    },
  },
  methods: {
    onLoaded(result: QueryResult) {
      this.reports = (result.items as Report[]);
      this.pagination.rowsNumber = result.total;
    },
    prepend(report: Report) {
      this.reports.unshift(report);
      this.pagination.rowsNumber += 1;
    },
    remove(id: number) {
      const i = this.reports.findIndex(r => r.id === id);
      if (i >= 0) {
        this.reports.splice(i, 1);
        this.pagination.rowsNumber -= 1;
      }
    },
  },
});
</script>
